/**
 * Batch Details Dialog Component
 * Comprehensive view of batch information with multiple tabs
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-002
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EditBatchDialog } from './EditBatchDialog';
import { UpdateQualityDialog } from './UpdateQualityDialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  BatchResponse,
  QualityGradeConfig,
  calculateDaysUntilExpiry,
  isExpiringSoon,
  isExpired,
  formatQuantity,
} from '@/types/inventory';
import { inventoryBatchService } from '@/services/commerce/inventoryBatchService';
import { BatchStatusBadge } from './BatchStatusBadge';
import { ProductTypeDisplay } from './ProductTypeDisplay';
import { ExpiryWarning } from './ExpiryWarning';
import {
  Package,
  AlertCircle,
  CheckCircle,
  Download,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { BatchQualityTab } from './tabs/BatchQualityTab';
import { BatchAllocationsTab } from './tabs/BatchAllocationsTab';
import { BatchAuditTrailTab } from './tabs/BatchAuditTrailTab';

interface BatchDetailsDialogProps {
  batchId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
  className?: string;
}

function InfoRow({ label, value, className }: InfoRowProps) {
  return (
    <div className={cn('flex justify-between items-start gap-4', className)}>
      <span className="text-sm text-gray-600 font-medium">{label}</span>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function BatchDetailsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-12 bg-gray-200 rounded animate-pulse" />
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-8 bg-gray-200 rounded animate-pulse" />
        ))}
      </div>
    </div>
  );
}

export function BatchDetailsDialog({
  batchId,
  open,
  onOpenChange,
}: BatchDetailsDialogProps) {
  const dialogTitleRef = useRef<HTMLHeadingElement>(null);
  const { data: batch, isLoading, error } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => {
      if (!batchId) throw new Error('Batch ID is required');
      return inventoryBatchService.getBatchById(batchId);
    },
    enabled: open && !!batchId,
    staleTime: 30 * 1000, // 30 seconds
  });

  const [activeTab, setActiveTab] = useState('details');
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isQualityDialogOpen, setIsQualityDialogOpen] = useState(false);

  // Focus management
  useEffect(() => {
    if (open && dialogTitleRef.current) {
      // Move focus to dialog title when opened
      dialogTitleRef.current.focus();
    }
  }, [open]);

  // Live region for loading states
  useEffect(() => {
    if (isLoading) {
      // Announce to screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = 'Loading batch details, please wait.';
      document.body.appendChild(announcement);
      return () => announcement.remove();
    }
  }, [isLoading]);

  if (!batch && !isLoading) {
    return null;
  }

  if (isLoading) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Loading Batch Details...</DialogTitle>
          </DialogHeader>
          <BatchDetailsSkeleton />
        </DialogContent>
      </Dialog>
    );
  }

  if (error || !batch) {
    const isNetworkError = error instanceof Error && error.message.includes('fetch');
    const isDemoMode = error instanceof Error && (error.message.toLowerCase().includes('unavailable') || error.message.toLowerCase().includes('demo'));

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Error Loading Batch</DialogTitle>
          </DialogHeader>
          <Alert variant={isDemoMode ? 'default' : 'destructive'} className={isDemoMode ? 'border-amber-300 bg-amber-50' : ''}>
            <AlertCircle className={cn("h-4 w-4", isDemoMode ? 'text-amber-600' : '')} />
            <AlertTitle className={isDemoMode ? 'text-amber-800' : ''}>
              {isDemoMode ? 'Demo Mode: Limited Data' : 'Failed to load batch details'}
            </AlertTitle>
            <AlertDescription className={isDemoMode ? 'text-amber-700' : ''}>
              {isDemoMode
                ? 'The backend service is currently unavailable. Showing sample batch data for demonstration purposes.'
                : error instanceof Error
                  ? error.message
                  : 'Please try again later.'}
            </AlertDescription>
          </Alert>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Calculate derived values
  const daysRemaining = calculateDaysUntilExpiry(batch.expiryDate);
  const expiryStatus = daysRemaining < 7 ? 'critical' : daysRemaining < 30 ? 'warning' : 'safe';
  const utilizationPercent = (batch.allocatedQuantity / batch.currentQuantity) * 100;
  const isExpiring = isExpiringSoon(batch.expiryDate);
  const isExp = isExpired(batch.expiryDate);
  const gradeConfig = QualityGradeConfig[batch.qualityGrade];

  // Helper to get expiry color
  const getExpiryColor = (status: 'critical' | 'warning' | 'safe') => {
    switch (status) {
      case 'critical':
        return 'text-red-600 font-bold';
      case 'warning':
        return 'text-orange-600 font-bold';
      case 'safe':
        return 'text-green-600';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-full sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle
            ref={dialogTitleRef}
            tabIndex={-1}
            className="flex items-center gap-3 text-xl focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2"
          >
            <Package className="w-6 h-6 text-blue-600" aria-hidden="true" />
            <span>Batch Details: {batch.batchNumber}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Alerts Section */}
        <div className="space-y-3">
          {/* PESO Compliance Alert */}
          {!batch.pesoCompliant && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>PESO Certification Required</AlertTitle>
              <AlertDescription>
                This batch is not PESO compliant and cannot be allocated to orders.
              </AlertDescription>
            </Alert>
          )}

          {/* Expiry Critical Alert */}
          {isExp && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Batch Expired</AlertTitle>
              <AlertDescription>
                This batch has expired and should not be used.
              </AlertDescription>
            </Alert>
          )}

          {/* Expiry Warning Alert */}
          {isExpiring && !isExp && (
            <Alert variant="default" className="border-orange-300 bg-orange-50">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <AlertTitle className="text-orange-800">Expiry Warning</AlertTitle>
              <AlertDescription className="text-orange-700">
                This batch will expire in {daysRemaining} days.
              </AlertDescription>
            </Alert>
          )}

          {/* Low Stock Alert */}
          {(batch.availableQuantity / batch.currentQuantity) * 100 < 20 && (
            <Alert variant="default" className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertTitle className="text-amber-800">Low Stock</AlertTitle>
              <AlertDescription className="text-amber-700">
                Available quantity is below 20%.
              </AlertDescription>
            </Alert>
          )}

          {/* Quality Issue Alert */}
          {(batch.qualityGrade === 'C') && (
            <Alert variant="default" className="border-yellow-300 bg-yellow-50">
              <AlertTriangle className="h-4 w-4 text-yellow-600" />
              <AlertTitle className="text-yellow-800">Quality Issue</AlertTitle>
              <AlertDescription className="text-yellow-700">
                Grade C quality batch. Handle with care.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Tabs Section */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4" role="tablist" aria-label="Batch information sections">
            <TabsTrigger value="details" aria-label="View batch details and information" role="tab">Details</TabsTrigger>
            <TabsTrigger value="quality" aria-label="View quality metrics and information" role="tab">Quality</TabsTrigger>
            <TabsTrigger value="allocations" aria-label={`View orders with allocations (${batch.allocations?.length || 0} orders)`} role="tab">
              Allocations
            </TabsTrigger>
            <TabsTrigger value="audit" aria-label="View audit trail and activity logs" role="tab">Audit Trail</TabsTrigger>
          </TabsList>

          {/* Details Tab */}
          <TabsContent value="details" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Batch Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Batch Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <InfoRow
                    label="Batch Number"
                    value={batch.batchNumber}
                  />
                  <InfoRow
                    label="Product Type"
                    value={<ProductTypeDisplay productType={batch.productType} variant="inline" />}
                  />
                  <InfoRow
                    label="Production Date"
                    value={format(new Date(batch.productionDate), 'MMM dd, yyyy')}
                  />
                  <InfoRow
                    label="Expiry Date"
                    value={
                      <div className="flex flex-col gap-1">
                        <span className={getExpiryColor(expiryStatus)}>
                          {format(new Date(batch.expiryDate), 'MMM dd, yyyy')}
                        </span>
                        <span className={cn(
                          'text-xs font-medium',
                          expiryStatus === 'safe' ? 'text-green-600' :
                          expiryStatus === 'warning' ? 'text-orange-600' : 'text-red-600'
                        )}>
                          ({daysRemaining} days remaining)
                        </span>
                      </div>
                    }
                  />
                  <InfoRow
                    label="Storage Location"
                    value={batch.storageLocation}
                  />
                  <InfoRow
                    label="Status"
                    value={<BatchStatusBadge status={batch.batchStatus} />}
                  />
                </CardContent>
              </Card>

              {/* Quantity Information Card */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Quantity Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Utilization Progress */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">
                        Utilization
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        {utilizationPercent.toFixed(1)}%
                      </span>
                    </div>
                    <Progress value={Math.min(utilizationPercent, 100)} className="h-2" />
                  </div>

                  {/* Quantity Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-xs text-gray-600 font-medium mb-1">Initial</div>
                      <div className="text-lg font-bold text-gray-900">
                        {batch.initialQuantity}
                      </div>
                      <div className="text-xs text-gray-500">
                        {batch.quantityUnit}
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="text-xs text-gray-600 font-medium mb-1">Current</div>
                      <div className="text-lg font-bold text-gray-900">
                        {batch.currentQuantity}
                      </div>
                      <div className="text-xs text-gray-500">
                        {batch.quantityUnit}
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="text-xs text-blue-600 font-medium mb-1">Allocated</div>
                      <div className="text-lg font-bold text-blue-900">
                        {batch.allocatedQuantity}
                      </div>
                      <div className="text-xs text-blue-600">
                        {batch.quantityUnit}
                      </div>
                    </div>
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="text-xs text-green-600 font-medium mb-1">Available</div>
                      <div className="text-lg font-bold text-green-900">
                        {batch.availableQuantity}
                      </div>
                      <div className="text-xs text-green-600">
                        {batch.quantityUnit}
                      </div>
                    </div>
                  </div>

                  {/* Formula Information */}
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700">
                      <span className="font-semibold">Formula:</span> Available = Current - Allocated
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* PESO Compliance Section */}
            {batch.pesoCompliant && batch.pesoCertificate && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    PESO Compliance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <InfoRow
                        label="Certificate Number"
                        value={batch.pesoCertificate.number}
                      />
                    </div>
                    <div>
                      <InfoRow
                        label="Inspector"
                        value={batch.pesoCertificate.inspector}
                      />
                    </div>
                    <div>
                      <InfoRow
                        label="Issue Date"
                        value={format(new Date(batch.pesoCertificate.issueDate), 'MMM dd, yyyy')}
                      />
                    </div>
                    <div>
                      <InfoRow
                        label="Expiry Date"
                        value={format(new Date(batch.pesoCertificate.expiryDate), 'MMM dd, yyyy')}
                      />
                    </div>
                  </div>
                  {batch.pesoCertificate.fileUrl && (
                    <div className="mt-4">
                      <Button variant="outline" size="sm" asChild>
                        <a
                          href={batch.pesoCertificate.fileUrl}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download Certificate
                        </a>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Quality Tab */}
          <TabsContent value="quality">
            <BatchQualityTab batch={batch} />
          </TabsContent>

          {/* Allocations Tab */}
          <TabsContent value="allocations">
            <BatchAllocationsTab batch={batch} />
          </TabsContent>

          {/* Audit Trail Tab */}
          <TabsContent value="audit">
            <BatchAuditTrailTab batchId={batch.id} />
          </TabsContent>
        </Tabs>

        {/* Footer with Action Buttons */}
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <DialogClose asChild>
            <Button variant="outline" aria-label="Close batch details dialog">
              Close
            </Button>
          </DialogClose>
          <Button
            variant="default"
            aria-label={`Edit batch ${batch.batchNumber}`}
            title="Edit batch details and information"
            onClick={() => setIsEditDialogOpen(true)}
          >
            Edit Batch
          </Button>
          <Button
            variant="outline"
            aria-label={`Update quality for batch ${batch.batchNumber}`}
            title="Update quality grade and purity percentage"
            onClick={() => setIsQualityDialogOpen(true)}
          >
            Update Quality
          </Button>
          {!batch.pesoCompliant && (
            <Button
              variant="secondary"
              aria-label={`Mark batch ${batch.batchNumber} as PESO compliant`}
              title="Add PESO certification to this batch"
            >
              Mark PESO Compliant
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Edit Batch Dialog */}
      <EditBatchDialog
        batch={batch}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        onSuccess={() => {
          // Refresh batch data if needed
        }}
      />

      {/* Update Quality Dialog */}
      <UpdateQualityDialog
        batch={batch}
        open={isQualityDialogOpen}
        onOpenChange={setIsQualityDialogOpen}
        onSuccess={() => {
          // Refresh batch data if needed
        }}
      />
    </Dialog>
  );
}
